import React, { useEffect, useState } from 'react'
import { ThemeProvider, MobileThemeProvider, MobileMode } from '@amzn/storm-ui'
import { AAC_MODAL_OPEN, AAC_MODAL_SETID, AAC_MODAL_CLOSE, AAC_FIT_UNREAD_FEATURE, AAC_FIT_NEW_FEATURES_LOADED } from '@amzn/intro-widgets-api'
import { StyleSheetManager } from 'styled-components'
import rtlPlugin from 'stylis-plugin-rtl'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { FeatureModal } from '../components/FeatureModal'
import { ajaxFetchFeatureData, FeatureResponse } from '../utils/fetchModalData'
import { identifyUnreadFeatures, addDissmissFeature, NoopPlugin } from '../utils/functions'
import { FeatureContextProvider } from '../contexts/FeatureContext'
import { logWithTakt } from '../utils/takt'
import { AAC_FIT_DISMISSED_WHATS_NEW_WIDGETS, WidgetTypes } from '../common/constants'
import { customizeTheme } from '../utils/palette'
import { WidgetStatus } from '../common/props'

export const WhatsNewCenter: React.FC<{ mobileView?: boolean; featureLabels?: any }> = ({ mobileView = false, featureLabels }) => {
  //setup context data that will be used in FeatureModal folder
  const [featureData, setFeatureData] = useState<{ [key: string]: FeatureResponse }>({})
  const [featureKeys, setFeatureKeys] = useState<string[]>([])
  const [unreadFeatures, setUnreadFeatures] = useState<Array<string>>([])
  const [featureOpen, setFeatureOpen] = useState(false)
  const [featureId, setFeatureId] = useState('')

  const featureCloseEvent = () => {
    setFeatureOpen(false)
    setFeatureId('')
    document.dispatchEvent(
      new CustomEvent(AAC_FIT_UNREAD_FEATURE, {
        detail: { newfeatures: unreadFeatures?.length, totalFeatures: featureKeys.length },
      })
    )
    logWithTakt(WidgetTypes.WhatsNewCenter, { event: 'close' })
  }

  const setFeatureIdEvent = (featureId) => {
    setFeatureId(featureId)
    logWithTakt(WidgetTypes.WhatsNewCenter, { event: 'setFeature', detail: { featureId } })
    if (featureId) addDissmissFeature(featureId)
    if (featureKeys.length > 0) setUnreadFeatures(identifyUnreadFeatures(featureKeys))
  }

  const chromeModalCloseEvent = () => {
    setFeatureOpen(false)
    logWithTakt(WidgetTypes.WhatsNewCenter, { event: 'close' })
  }

  //add event listener and load data from ajax call
  useEffect(() => {
    document.addEventListener(AAC_MODAL_CLOSE, chromeModalCloseEvent)

    const loadData = async () => {
      const result = await ajaxFetchFeatureData({ endpoint: '/ccx/ajax/new-features' })
      if (result?.data?.length < 1) return
      const featureData = result.data.reduce((features, obj) => {
        return { ...features, [obj.anchorId]: obj }
      }, {})
      setFeatureData(featureData)
      setFeatureKeys(Object.keys(featureData))
      const dismissedWidgets = Object.keys(featureData).filter((key) => featureData[key].widgetStatus === WidgetStatus.DISMISSED)
      localStorage.setItem(AAC_FIT_DISMISSED_WHATS_NEW_WIDGETS, JSON.stringify(dismissedWidgets))
    }
    loadData()

    return () => {
      document.removeEventListener(AAC_MODAL_CLOSE, chromeModalCloseEvent)
    }
  }, [])

  useEffect(() => {
    const featureOpenEvent = () => {
      setFeatureOpen(true)
      logWithTakt(WidgetTypes.WhatsNewCenter, { event: 'open', detail: { featureList: featureKeys } })
    }
    const chromeModalSetIdEvent = (event: any) => {
      if (!featureOpen) {
        featureOpenEvent()
      }
      setFeatureIdEvent(event?.detail?.id ?? '')
    }
    document.addEventListener(AAC_MODAL_OPEN, featureOpenEvent)
    document.addEventListener(AAC_MODAL_SETID, chromeModalSetIdEvent)
    return () => {
      document.removeEventListener(AAC_MODAL_OPEN, featureOpenEvent)
      document.removeEventListener(AAC_MODAL_SETID, chromeModalSetIdEvent)
    }
  }, [featureKeys])

  useEffect(() => {
    const unreadFeatures = identifyUnreadFeatures(featureKeys)
    setUnreadFeatures(unreadFeatures)
    // This event will be deprecated after the AAC_FIT_NEW_FEATURES_LOADED event is picked up on the AACChromeUI side
    document.dispatchEvent(
      new CustomEvent(AAC_FIT_UNREAD_FEATURE, {
        detail: { newfeatures: unreadFeatures?.length ?? 0, totalFeatures: featureKeys.length },
      })
    )

    document.dispatchEvent(
      new CustomEvent(AAC_FIT_NEW_FEATURES_LOADED, {
        detail: { newfeatures: unreadFeatures?.length ?? 0, totalFeatures: featureKeys.length },
      })
    )
  }, [featureKeys, featureOpen])

  return (
    <ErrorBoundary anchorId="FITManager" mobileView={mobileView}>
      <ThemeProvider>
        <ThemeProvider theme={customizeTheme}>
          <MobileThemeProvider mobileMode={mobileView ? MobileMode.Enable : MobileMode.Disable}>
            <StyleSheetManager stylisPlugins={document.documentElement.dir === 'rtl' ? [rtlPlugin, NoopPlugin] : [NoopPlugin]}>
              <FeatureContextProvider value={{ featureData, featureKeys, unreadFeatures, featureOpen, featureId, featureLabels, featureCloseEvent, setFeatureIdEvent }}>
                <FeatureModal isMobile={mobileView} />
              </FeatureContextProvider>
            </StyleSheetManager>
          </MobileThemeProvider>
        </ThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
